import React, { useState, useEffect } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Icon } from 'ui';
import isEqual from 'lodash.isequal';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { setMappedColumnDefs, updateSort } from '../../worklist/containers/worklist-actions';
import Table from '../table/table';
import SortButton from '../../workgrid/components/sort-button';

const HideIcon = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 3px;
  border: 2px solid ${p => p.theme.colors.theme};
  background-color: ${p => p.isHidden ? '#fff' : p.theme.colors.theme};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${p => p.isHidden ? '0.5' : '1'};
  i {
    color: ${p => !p.isHidden ? '#fff' : p.theme.colors.theme};
    cursor: pointer;
  }
`;

const ResetIcon = styled.span`
  width: 25px;
  height: 25px;
  border-radius: 3px;
  background-color: ${p => p.theme.colors.theme};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 10px;
  i {
    color: white;
    cursor: pointer;
  }
`;

const Text = styled.p`
  opacity: ${p => p.isHidden ? '0.5' : '1'};
`;

const sort = (a, b) => {
  if (!a.isHidden && b.isHidden) {
    return -1;
  } else if (a.isHidden && !b.isHidden) {
    return 1;
  } else if ((!a.isHidden && !b.isHidden) || (a.isHidden && b.isHidden)) {
    return a.index - b.index;
  }
  return a.headerName?.localeCompare(b.headerName);
};

const ColumnSidepanel = ({ localData }) => {
  const dispatch = useDispatch();
  const mappedColumnDefs = useSelector(state => state.worklist.mappedColumns || []);
  const { defaultSorting } = useSelector(state => state.worklist.metaData);
  const { activeSort } = useSelector(state => state.worklist);
  const [cols, setCols] = useState();

  useEffect(() => {
    if (localData) {
      setCols(localData.columns);
    }
  }, [JSON.stringify(localData)]);

  useDeepCompareEffect(() => {
    if (
      !localData &&
      (!cols || !isEqual(cols, mappedColumnDefs)) &&
      mappedColumnDefs?.filter(f => !f.suppressMovable)?.length
    ) {
      const params = JSON.parse(localStorage.getItem('columnParams'));
      setCols(mappedColumnDefs
        ?.map(c => ({
          ...c,
          id: c.colId,
          index: params?.find(p => p.id === c.colId)?.index || c.index,
          width: params?.find(p => p.id === c.colId)?.width || c.width,
        }))
        ?.sort(sort),
      );
    }
  }, [mappedColumnDefs]);

  const handleRowVisibilityChange = (row) => {
    let hiddenElements = cols?.filter(f => f.isHidden && f.colId !== row.colId);
    let visibleElements = cols
      ?.sort((a, b) => a.index - b.index)
      ?.filter(f => !f.isHidden && !f.suppressMovable && f.colId !== row.colId);
    const fixedCols = cols?.filter(c => c.suppressMovable)?.sort((a, b) => a.index - b.index);
    const fixedLeftCols = fixedCols?.filter(f => f.lockPosition === 'left');

    if (row.isHidden) {
      const last = visibleElements?.length ?
        visibleElements[visibleElements.length - 1] :
        fixedLeftCols[fixedLeftCols.length - 1];

      visibleElements = [
        ...visibleElements,
        {
          ...row,
          index: last ? last.index + 1 : 0,
          isHidden: false,
        },
      ];
    } else {
      hiddenElements = [
        ...hiddenElements,
        {
          ...row,
          index: hiddenElements.length,
          isHidden: true,
        },
      ]?.sort((a, b) => a.headerName?.localeCompare(b.headerName));
    }
    visibleElements = [
      ...visibleElements,
      ...fixedCols,
    ]?.sort((a, b) => a.index - b.index);

    const data = [...visibleElements, ...hiddenElements]?.map((d, i) => ({
      ...d,
      index: i,
    }));
    setCols(data);
    dispatch(setMappedColumnDefs({
      data,
      refetch: false,
    }));
  };

  const handleDrop = (e, id) => {
    const list = [
      ...e,
      ...cols?.filter(c => c.suppressMovable),
    ]?.sort(sort);
    setCols(list);
    if (localData) {
      const column = e.find(c => c.id === id);
      localData.onChange({
        column: {
          ...column,
          colDef: { id },
        },
        toIndex: e.find(c => c.id === id)?.index,
      });
    } else {
      dispatch(setMappedColumnDefs({ data: list, refetch: false }));
    }
  };

  const columns = [
    {
      id: 'showhide',
      displayName: '',
      width: '50px',
      renderFn: row => <HideIcon
        isHidden={row.isHidden}
        onClick={() => localData ?
          localData.onChange({
            column: {
              colDef: { id: row.id },
              hide: !row.isHidden,
            },
          }) :
          handleRowVisibilityChange(row)}
      >
        <Icon className={`fa-solid fa-eye${row.isHidden ? '-slash' : ''}`} size='sm' />
      </HideIcon>,
    },
    {
      id: 'column',
      displayName: <FormattedMessage id='worklist.filters.COLUMN' />,
      renderFn: row => <Text isHidden={row.isHidden}>{row.headerName}</Text>,
    },
    {
      id: 'sorting',
      displayName: <>
        <div><FormattedMessage id='worklist.filters.SORTING' /></div>
        <ResetIcon
          onClick={() => localData ?
            localData.onSort(localData.defaultSorting) :
            dispatch(updateSort(defaultSorting))}
        >
          <Icon type='rotate-left' size='sm' />
        </ResetIcon>
      </>,
      renderFn: row => row.sortable ?
        <SortButton
          id={row.colId}
          icons={row.icons}
          activeSort={localData ? localData.activeSort : activeSort}
          updateCb={localData ? localData.onSort : updateSort}
          defaultSorting={localData ? localData.defaultSorting : defaultSorting}
          isLocal={!!localData}
        /> :
        null,
    },
  ];

  return (
    <Table
      columns={columns}
      data={cols?.filter(c => !c.suppressMovable) || []}
      lightHeader={true}
      movableRows={true}
      onDrop={handleDrop}
      maxHeight={`calc(100vh - ${localData ? 120 : 250}px)`}
    />
  );
};

ColumnSidepanel.propTypes = {
  localData: PropTypes.object,
};

export default ColumnSidepanel;
